import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Canonical 0–1 bar position for each spec-defined level.
// Anchor points from agents/argus/spec.yaml.
const LEVEL_POSITION = {
    // Financial sophistication (spec: Novice→Developing→Competent→Advanced→Expert)
    novice: 0.10, developing: 0.30, competent: 0.50, advanced: 0.70, expert: 0.90,
    // Context density (spec: Transactional→Goal-Oriented→Values-Integrated→Identity-Integrated)
    transactional: 0.10, goal_oriented: 0.38, values_integrated: 0.65, identity_integrated: 0.90,
    // Decision style
    delegator: 0.10, collaborator: 0.38, analytical: 0.70, validator: 0.90,
    // Risk behaviour (spec: Preservationist→Conservative→Moderate→Growth→Speculative)
    preservationist: 0.10, conservative: 0.30, moderate: 0.50, growth: 0.70, speculative: 0.90,
    // Temporal orientation
    short_term: 0.10, mid_term: 0.50, long_term: 0.90,
};
const DIMENSIONS = [
    {
        key: "sophistication",
        confidenceKey: "sophistication",
        label: "Financial sophistication",
        lowLabel: "Novice",
        highLabel: "Expert",
        description: "Observed from vocabulary, concept comfort, and self-correction patterns.",
    },
    {
        key: "context_density",
        confidenceKey: "context_density",
        label: "Context density",
        lowLabel: "Transactional",
        highLabel: "Identity-Integrated",
        description: "How much life context, goals, and values you bring into conversations.",
    },
    {
        key: "decision_style",
        confidenceKey: "decision_style",
        label: "Decision style",
        lowLabel: "Delegator",
        highLabel: "Validator",
        description: "Whether you prefer Luna to decide, collaborate, or validate your own thinking.",
    },
    {
        key: "risk_behavior",
        // In-memory ShadowProfile uses risk_tolerance as the field name →
        // confidence_scores dict key is also "risk_tolerance".
        confidenceKey: "risk_tolerance",
        label: "Risk behaviour",
        lowLabel: "Preservationist",
        highLabel: "Speculative",
        description: "Inferred from reactions to loss scenarios, volatility, and time horizon statements.",
    },
    {
        key: "temporal_orientation",
        confidenceKey: "temporal_orientation",
        label: "Temporal orientation",
        lowLabel: "Short-term",
        highLabel: "Long-term",
        description: "Planning horizon from immediate cash needs to multi-decade wealth strategy.",
    },
];
function formatLevel(level) {
    if (!level)
        return "";
    return level.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function relativeTime(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 3_600_000)
        return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000)
        return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
}
export function ShadowProfilePanel({ profile, isLoading = false }) {
    if (isLoading) {
        return (_jsx("div", { className: "bg-slate-900 border border-slate-800 rounded-2xl p-6", children: _jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-600", children: [_jsx("svg", { className: "animate-spin", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }) }), "Loading behavioural profile\u2026"] }) }));
    }
    if (!profile) {
        return (_jsxs("div", { className: "bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center space-y-2", children: [_jsx("p", { className: "text-sm text-slate-500", children: "Behavioural profile not yet built." }), _jsx("p", { className: "text-xs text-slate-700", children: "Have a few conversations with Luna \u2014 Argus will build your profile from the patterns." })] }));
    }
    return (_jsxs("div", { className: "bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5", children: [_jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-sm font-semibold text-slate-200", children: "How Argus sees you" }), _jsx("p", { className: "text-xs text-slate-600 mt-0.5", children: "Inferred silently from conversation patterns. Never entered manually." })] }), profile.last_updated && (_jsxs("span", { className: "text-[10px] text-slate-700 shrink-0", children: ["Updated ", relativeTime(profile.last_updated)] }))] }), _jsx("div", { className: "space-y-5", children: DIMENSIONS.map(({ key, confidenceKey, label, lowLabel, highLabel, description }) => {
                    const level = profile[key];
                    const position = level != null ? (LEVEL_POSITION[level] ?? 0.5) : null;
                    const confidence = profile.confidence[confidenceKey] ?? 0;
                    return (_jsxs("div", { className: "space-y-1.5", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-xs font-medium text-slate-400", children: label }), level != null ? (_jsx("span", { className: "text-xs font-semibold text-violet-400", children: formatLevel(level) })) : (_jsx("span", { className: "text-xs text-slate-600 italic", children: "not yet observed" }))] }), position != null ? (_jsxs("div", { className: "relative", children: [_jsx("div", { className: "h-1.5 w-full rounded-full bg-slate-800" }), _jsx("div", { className: "absolute top-0 left-0 h-1.5 rounded-full bg-violet-600/70", style: { width: `${position * 100}%` } }), _jsx("div", { className: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-violet-400 border-2 border-slate-900", style: { left: `${position * 100}%` } })] })) : (_jsx("div", { className: "h-1.5 w-full rounded-full bg-slate-800" })), _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-[9px] text-slate-700", children: lowLabel }), confidence > 0 && (_jsxs("span", { className: "text-[9px] text-slate-700", children: [Math.round(confidence * 100), "% confidence"] })), _jsx("span", { className: "text-[9px] text-slate-700", children: highLabel })] }), _jsx("p", { className: "text-[10px] text-slate-600", children: description })] }, key));
                }) })] }));
}
